// src/pages/Dashboard.js — Escritorio del CMS
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Spin, Alert, Segmented, Empty } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  SoundOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  HeartOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import { articlesAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { neutral, statusColors } from '../styles/theme';
import { getArticleStatus, STATUS_LABELS } from '../utils/articleStatus';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const SECTION_CHIPS_COLLAPSED = 12;

const greetingFor = (hour) => {
  if (hour < 6) return 'Buenas noches';
  if (hour < 14) return 'Buenos días';
  if (hour < 21) return 'Buenas tardes';
  return 'Buenas noches';
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllSections, setShowAllSections] = useState(false);
  const [recentTab, setRecentTab] = useState('Últimos');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const statsResponse = await articlesAPI.getStats();
      setStats(statsResponse.data);

      // Mismo límite que venía usando el CMS: la carga completa de artículos
      // trae el contenido de cada uno y subirlo hace lento el arranque.
      const articlesResponse = await articlesAPI.getArticles();
      setArticles((articlesResponse.data || []).filter((article) => !article.deleted_at));
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Error al cargar los datos del escritorio');
    } finally {
      setLoading(false);
    }
  };

  const goToSection = (sectionName) => navigate(`/articles?section=${encodeURIComponent(sectionName)}`);
  const goToArticles = () => navigate('/articles');
  const goToCreateArticle = () => navigate('/articles/new');

  const handleSearch = (value) => {
    const term = (value || '').trim();
    navigate(term ? `/articles?search=${encodeURIComponent(term)}` : '/articles');
  };

  // Artículos clasificados por su estado efectivo, una sola pasada.
  const buckets = useMemo(() => {
    const result = { published: [], draft: [], scheduled: [], active: [] };
    articles.forEach((article) => {
      result[getArticleStatus(article)].push(article);
    });
    return result;
  }, [articles]);

  const kpis = useMemo(() => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const startOfPrevMonth = startOfMonth.subtract(1, 'month');

    const publishedThisMonth = buckets.published.filter((a) =>
      dayjs(a.date || a.created_at).isSame(now, 'month')
    ).length;

    const publishedPrevMonth = buckets.published.filter((a) => {
      const d = dayjs(a.date || a.created_at);
      return d.isSame(startOfPrevMonth, 'month');
    }).length;

    const nextScheduled = [...buckets.scheduled]
      .filter((a) => a.date && dayjs(a.date).isAfter(now))
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())[0];

    const publishedThisWeek = buckets.published.filter((a) =>
      dayjs(a.date || a.created_at).isAfter(now.startOf('week'))
    ).length;

    const monthDelta = publishedThisMonth - publishedPrevMonth;

    return {
      publishedThisMonth,
      monthDelta,
      drafts: buckets.draft.length,
      scheduled: buckets.scheduled.length,
      nextScheduled,
      total: stats?.total_articles ?? articles.length,
      sections: stats?.total_sections ?? 0,
      publishedThisWeek
    };
  }, [buckets, stats, articles.length]);

  const recentArticles = useMemo(() => {
    const byDateDesc = (a, b) =>
      dayjs(b.date || b.created_at).valueOf() - dayjs(a.date || a.created_at).valueOf();

    if (recentTab === 'Borradores') {
      return [...buckets.draft]
        .sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf())
        .slice(0, 5);
    }
    if (recentTab === 'Programados') {
      // Los programados se leen en orden de salida: primero el más próximo.
      return [...buckets.scheduled]
        .sort((a, b) => dayjs(a.date || a.created_at).valueOf() - dayjs(b.date || b.created_at).valueOf())
        .slice(0, 5);
    }
    // "Últimos" y "Publicados" comparten criterio: publicados con fecha ya
    // alcanzada, del más reciente al más antiguo.
    return [...buckets.published].sort(byDateDesc).slice(0, 5);
  }, [buckets, recentTab]);

  const visibleSections = useMemo(() => {
    const all = (stats?.sections || []).filter(
      (section) =>
        section?.name && !section.name.startsWith('Camino') && section.name !== 'General'
    );
    return showAllSections ? all : all.slice(0, SECTION_CHIPS_COLLAPSED);
  }, [stats, showAllSections]);

  const totalSectionChips = useMemo(
    () =>
      (stats?.sections || []).filter(
        (section) =>
          section?.name && !section.name.startsWith('Camino') && section.name !== 'General'
      ).length,
    [stats]
  );

  const quickActions = [
    { key: 'create', label: 'Crear artículo', icon: <PlusOutlined />, onClick: goToCreateArticle, featured: true },
    { key: 'manage', label: 'Gestionar contenido', icon: <UnorderedListOutlined />, onClick: goToArticles },
    { key: 'calendar', label: 'Ver calendario', icon: <CalendarOutlined />, onClick: () => navigate('/calendar') },
    { key: 'mottos', label: 'Ver lemas', icon: <HeartOutlined />, onClick: () => navigate('/mottos') },
    { key: 'trash', label: 'Papelera', icon: <DeleteOutlined />, onClick: () => navigate('/trash') }
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Cargando escritorio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  const today = dayjs();

  return (
    <div className="dashboard-page">
      {/* Cabecera */}
      <header className="dash-header">
        <div className="dash-header-text">
          <div className="dash-kicker">CMS · AGUSTINOS</div>
          <h1 className="dash-greeting">
            {greetingFor(today.hour())}, {user?.username || 'editor'}
          </h1>
          <p className="dash-subtitle">
            {capitalize(today.format('dddd, D [de] MMMM [de] YYYY'))}
            {' · '}
            {kpis.publishedThisWeek}{' '}
            {kpis.publishedThisWeek === 1 ? 'artículo publicado' : 'artículos publicados'} esta semana
          </p>
        </div>
        <div className="dash-header-actions">
          <Input.Search
            placeholder="Buscar artículo…"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={handleSearch}
            className="dash-search"
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={goToCreateArticle} className="dash-create">
            Crear nuevo
          </Button>
        </div>
      </header>

      {/* KPIs */}
      <section className="kpi-strip">
        <div className="kpi-cell">
          <span className="kpi-label">
            Publicados este mes <CheckCircleOutlined style={{ color: statusColors.published.fg }} />
          </span>
          <span className="kpi-value">{kpis.publishedThisMonth}</span>
          <span className="kpi-note" style={{ color: statusColors.published.fg }}>
            {kpis.monthDelta === 0
              ? 'igual que el mes anterior'
              : `${kpis.monthDelta > 0 ? '↑' : '↓'} ${Math.abs(kpis.monthDelta)} vs. mes anterior`}
          </span>
        </div>
        <div className="kpi-cell">
          <span className="kpi-label">
            Borradores pendientes <EditOutlined style={{ color: statusColors.scheduled.fg }} />
          </span>
          <span className="kpi-value">{kpis.drafts}</span>
          <span className="kpi-note" style={{ color: statusColors.scheduled.fg }}>
            {kpis.drafts === 0 ? 'nada pendiente' : 'por revisar'}
          </span>
        </div>
        <div className="kpi-cell">
          <span className="kpi-label">
            Programados <ClockCircleOutlined style={{ color: statusColors.info.fg }} />
          </span>
          <span className="kpi-value">{kpis.scheduled}</span>
          <span className="kpi-note" style={{ color: statusColors.info.fg }}>
            {kpis.nextScheduled
              ? `próximo: ${dayjs(kpis.nextScheduled.date).format('D MMM')}`
              : 'sin programar'}
          </span>
        </div>
        <div className="kpi-cell">
          <span className="kpi-label">
            Total de artículos <FileTextOutlined style={{ color: statusColors.total.fg }} />
          </span>
          <span className="kpi-value">{kpis.total}</span>
          <span className="kpi-note" style={{ color: neutral.soft }}>
            en {kpis.sections} secciones
          </span>
        </div>
      </section>

      {/* Artículos recientes + acciones rápidas */}
      <div className="dash-columns">
        <section className="dash-card dash-recent">
          <div className="dash-card-head">
            <Segmented
              size="small"
              value={recentTab}
              onChange={setRecentTab}
              options={['Últimos', 'Publicados', 'Borradores', 'Programados']}
            />
            <button type="button" className="dash-link" onClick={goToArticles}>
              Ver todos <ArrowRightOutlined />
            </button>
          </div>

          <div className="recent-head-row">
            <span />
            <span>Artículo</span>
            <span>Sección</span>
            <span>Fecha</span>
            <span>Estado</span>
            <span />
          </div>

          {recentArticles.length === 0 ? (
            <Empty
              description="No hay artículos en esta pestaña"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: '28px 0' }}
            />
          ) : (
            recentArticles.map((article) => {
              const status = getArticleStatus(article);
              const palette = statusColors[status] || statusColors.draft;
              return (
                <div
                  key={article.id}
                  className="recent-row"
                  onClick={() => navigate(`/articles/edit/${article.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/articles/edit/${article.id}`);
                  }}
                >
                  {article.imageUrl ? (
                    <img src={article.imageUrl} alt="" className="recent-thumb" />
                  ) : (
                    <div className="recent-thumb recent-thumb--empty">
                      <FileTextOutlined />
                    </div>
                  )}
                  <div className="recent-title-wrap">
                    <span className="recent-title">{article.title}</span>
                    {article.hasAudio && <SoundOutlined className="recent-audio" />}
                  </div>
                  {/* display:contents en escritorio: los tres chips caen en la
                      rejilla de la fila. En móvil se agrupan bajo el título. */}
                  <div className="recent-meta">
                    <span className="section-chip">{article.section || 'Sin sección'}</span>
                    <span className="recent-date">
                      {dayjs(article.date || article.created_at).format('D MMM YYYY')}
                    </span>
                    <span
                      className="status-chip"
                      style={{ background: palette.bg, color: palette.fg }}
                    >
                      {STATUS_LABELS[status]}
                    </span>
                  </div>
                  <span className="recent-edit">
                    <EditOutlined />
                  </span>
                </div>
              );
            })
          )}
        </section>

        <section className="dash-card dash-actions">
          <div className="dash-card-title">Acciones rápidas</div>
          <div className="quick-actions">
            {quickActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className={`quick-action${action.featured ? ' quick-action--featured' : ''}`}
                onClick={action.onClick}
              >
                <span className="quick-action-icon">{action.icon}</span>
                {action.label}
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Secciones */}
      <section className="dash-card dash-sections">
        <div className="dash-card-head">
          <span className="dash-card-title">Secciones</span>
          <button type="button" className="dash-link" onClick={goToArticles}>
            Ver todas
          </button>
        </div>
        <div className="section-chips">
          {visibleSections.map((section) => (
            <button
              key={section.name}
              type="button"
              className="section-pill"
              onClick={() => goToSection(section.name)}
            >
              <span className="section-initial">{section.name[0]}</span>
              <span className="section-name">{section.name}</span>
              <span className="section-count">{section.count ?? section.total_articles ?? ''}</span>
            </button>
          ))}
          {totalSectionChips > SECTION_CHIPS_COLLAPSED && (
            <button
              type="button"
              className="section-pill section-pill--more"
              onClick={() => setShowAllSections(!showAllSections)}
            >
              {showAllSections ? (
                <>
                  Ver menos <UpOutlined />
                </>
              ) : (
                <>
                  Ver todas ({totalSectionChips - SECTION_CHIPS_COLLAPSED} más) <DownOutlined />
                </>
              )}
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
